import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { useApi } from 'app/shared/contexts/api';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { UiMetadata, GetUiMetadataQueryResponse, UiMetadataParams } from 'app/data/types/ui-metadata';
import { useOpenParams } from 'app/shared/hooks/use-open-params/use-open-params.hook';
import { OwnerType, Verticals } from 'app/shared/consts/enums';
import { useEffect } from 'react';
import { useErrors } from 'app/shared/contexts/errors';
import { ERROR_DEFAULT } from 'app/shared/consts/error-codes';

export const UI_METADATA_QUERY_KEY = 'ui-metadata';

export const getUseUiMetadataQueryKey = (ownerType: string, ownerId: string): QueryKey => [
  UI_METADATA_QUERY_KEY,
  ownerType,
  ownerId,
];

export const useUiMetadataQuery = (params?: UiMetadataParams): GetUiMetadataQueryResponse => {
  const { addError, hideErrors } = useErrors();
  const { apiUrl, getAuthHeaders, getDefaultHeaders } = useApi();
  const { buildingUuid } = useOpenParams();

  const ownerType = params?.ownerType || OwnerType.BUILDING;
  const ownerId = params?.ownerId || buildingUuid;

  const queryKey = getUseUiMetadataQueryKey(ownerType, ownerId);

  const getUiMetadataRequest = async (): Promise<UiMetadata | null> => {
    if (!ownerId) {
      return null;
    }

    const headers = await getAuthHeaders();
    const requestHeaders = { ...headers, ...getDefaultHeaders() };

    const response = await apiRequest<UiMetadata, void, void>({
      url: `${apiUrl}/api/verticals/${Verticals.RESOURCE_BOOKING}/${ownerType.toUpperCase()}/${ownerId}/ui-metadata`,
      method: HttpMethod.GET,
      headers: requestHeaders,
    });

    if (response && !response.isEmpty()) {
      return response.data;
    }

    return null;
  };

  const { data, error, isLoading, isSuccess } = useQuery<UiMetadata | null, Error>({
    queryKey,
    queryFn: getUiMetadataRequest,
    enabled: !!ownerId && !!apiUrl && !!getAuthHeaders && !!getDefaultHeaders,
    retry: false,
  });

  useEffect(() => {
    if (error) {
      addError(UI_METADATA_QUERY_KEY, { error, errorCode: ERROR_DEFAULT });
    } else {
      hideErrors(UI_METADATA_QUERY_KEY);
    }
  }, [error, addError, hideErrors]);

  return {
    uiMetadata: data ?? null,
    error: error ? { error, errorCode: ERROR_DEFAULT } : null,
    isLoading,
    isSuccess,
  };
};
